import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BrandLogo } from "@/components/brand/BrandLogo";
import { Loader2, AlertCircle, Eye, EyeOff, ShieldCheck, Users, Zap } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok) navigate("/");
    else setError(res.error);
  };

  return (
    <div className="flex min-h-screen">
      {/* Brand panel */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-sidebar p-12 lg:flex">
        <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full bg-brand/25 blur-3xl" />
        <div className="absolute -bottom-32 -left-16 h-96 w-96 rounded-full bg-brand/10 blur-3xl" />
        <div className="relative">
          <BrandLogo onDark widthClass="w-[340px] max-w-[82%]" />
        </div>
        <div className="relative">
          <h1 className="font-display text-4xl font-bold leading-tight tracking-tight text-white">
            Un unico posto<br />per ogni cliente.
          </h1>
          <p className="mt-4 max-w-md leading-relaxed text-sidebar-muted">
            Luce &amp; Gas ed Efficienza Energetica in un solo CRM. Nessun cliente dimenticato,
            nessuna scadenza persa, sempre la prossima azione sotto controllo.
          </p>
          <div className="mt-8 grid max-w-sm grid-cols-3 gap-4">
            {[
              { Icon: Zap, t: "Commodity" },
              { Icon: Users, t: "360° Cliente" },
              { Icon: ShieldCheck, t: "GDPR" },
            ].map((x) => (
              <div key={x.t} className="rounded-xl border border-white/10 bg-white/5 p-3">
                <x.Icon className="mb-1.5 h-5 w-5 text-brand" />
                <div className="text-xs font-medium text-white">{x.t}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative text-xs text-sidebar-muted">© {new Date().getFullYear()} ESSECI E-LIGHT</div>
      </div>

      {/* Form */}
      <div className="flex w-full flex-col justify-center bg-gradient-to-b from-sky-50/70 via-white to-white px-6 py-12 sm:px-12 lg:w-1/2">
        <div className="mx-auto w-full max-w-sm animate-fade-in-up">
          <div className="mb-8 flex justify-center lg:hidden">
            <BrandLogo widthClass="w-64" />
          </div>

          <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900">Area Riservata</h2>
          <p className="mt-1.5 text-sm font-semibold uppercase tracking-[0.18em] text-brand">CRM ESSECI E-LIGHT</p>
          <p className="mt-2 text-sm text-slate-500">Accedi con le tue credenziali operatore.</p>

          {error && (
            <div className="mt-5 flex items-start gap-2 rounded-lg border border-danger/25 bg-danger/10 px-3 py-2.5 text-sm text-danger" data-testid="login-error">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" autoComplete="username" required value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="nome@esseci-elight.it"
                data-testid="login-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input id="password" type={showPwd ? "text" : "password"} autoComplete="current-password" required
                  value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                  data-testid="login-password-input" className="pr-10" />
                <button type="button" onClick={() => setShowPwd((v) => !v)} tabIndex={-1}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-slate-600"
                  data-testid="toggle-password">
                  {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit-button" className="w-full">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "ACCEDI"}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <Link to="/forgot-password" className="text-sm font-medium text-brand transition-colors hover:text-brand-strong" data-testid="forgot-password-link">
              Password dimenticata?
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
